/**
 * Drop Zone Component
 *
 * A reusable drop zone for file drag-and-drop with visual feedback.
 */

import { useCallback, useState } from 'react';
import { useFileStore, isIDFFile } from '../index';

interface DropZoneProps {
  children: React.ReactNode;
  className?: string;
  onDrop?: (file: File) => void;
}

export function DropZone({ children, className = '', onDrop }: DropZoneProps) {
  const [isDragging, setIsDragging] = useState(false);
  const { importFile, setImportDialogOpen } = useFileStore();

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    // Only handle file drags, let component drags pass through to canvas
    const hasFiles = e.dataTransfer.types.includes('Files');
    if (!hasFiles) return;

    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    // Only handle file drags, let component drags pass through to canvas
    const hasFiles = e.dataTransfer.types.includes('Files');
    if (!hasFiles) return;

    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // Only handle file drags, let component drags pass through to canvas
    const hasFiles = e.dataTransfer.types.includes('Files');
    if (!hasFiles) return;

    e.preventDefault();
    e.stopPropagation();

    // Only set dragging to false if we're leaving the drop zone entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setIsDragging(false);
    }
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      // Only handle file drops, let component drops pass through to canvas
      const hasFiles = e.dataTransfer.types.includes('Files');
      if (!hasFiles) return;

      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        if (file) {
          if (onDrop) {
            onDrop(file);
          } else if (isIDFFile(file.name)) {
            await importFile(file);
            setImportDialogOpen(true);
          }
        }
      }
    },
    [onDrop, importFile, setImportDialogOpen]
  );

  return (
    <div
      className={`relative ${className}`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {children}

      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-50 flex items-center justify-center rounded-lg border-2 border-dashed border-primary bg-primary/10">
          <div className="flex flex-col items-center gap-2 text-primary">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span className="font-medium">Drop IDF file to import</span>
          </div>
        </div>
      )}
    </div>
  );
}
